import { itemCardStatusFromListing } from "@/lib/item-card-status";

export type SavedItemAction = "buy" | "offer" | "bid" | "notify" | "find_similar";

export type SavedItemRowListing = {
  id: string;
  title: string;
  price: number;
  currency?: string | null;
  image_urls?: string[];
  condition?: string | null;
  grade?: string | number | null;
  quantity?: number | null;
  listing_format?: string | null;
  type?: string | null;
  scheduled_publish_at?: string | null;
  auction_start_price?: number | null;
  auction_highest_bid?: number | null;
  auction_status?: string | null;
};

export function isAuctionSavedListing(item: SavedItemRowListing): boolean {
  const fmt = String(item.listing_format ?? "").toLowerCase();
  if (fmt === "auction") return true;
  return (
    item.auction_start_price != null &&
    fmt !== "fixed_price" &&
    fmt !== "fixed_price_with_offers" &&
    fmt !== "offers_only"
  );
}

export function effectiveSavedListingPrice(item: SavedItemRowListing): number {
  if (!isAuctionSavedListing(item)) return item.price;
  const hb = item.auction_highest_bid;
  const sp = item.auction_start_price;
  if (hb != null && Number.isFinite(hb) && hb > 0) return hb;
  if (sp != null && Number.isFinite(sp)) return sp;
  return item.price;
}

function listingTypeForCard(item: SavedItemRowListing): "standard" | "preorder" | null {
  return item.type === "preorder" || item.type === "standard"
    ? (item.type as "standard" | "preorder")
    : null;
}

export function deriveSavedItemAction(item: SavedItemRowListing): SavedItemAction {
  if (typeof item.quantity === "number" && item.quantity <= 0) return "find_similar";

  const auctionStatusRaw = String(item.auction_status ?? "").toLowerCase();
  const auctionEnded =
    auctionStatusRaw === "ended" || auctionStatusRaw === "settled";

  const cardStatus = itemCardStatusFromListing({
    scheduled_publish_at: item.scheduled_publish_at,
    listing_format: item.listing_format,
    listing_type: listingTypeForCard(item),
  });

  if (cardStatus === "auction") {
    return auctionEnded ? "find_similar" : "bid";
  }
  if (cardStatus === "offers") return "offer";
  if (cardStatus === "scheduled") return "notify";
  return "buy";
}

export function primarySavedListingImage(urls: SavedItemRowListing["image_urls"]): string | null {
  if (!urls) return null;
  for (const u of urls) {
    if (typeof u === "string" && u.length > 0) return u;
  }
  return null;
}

export function formatSavedConditionLabel(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function shouldShowSavedGrade(
  item: Pick<SavedItemRowListing, "condition" | "grade">,
): boolean {
  if (item.grade == null) return false;
  const raw = String(item.grade).trim();
  if (raw.length === 0) return false;
  if (String(item.condition ?? "").toLowerCase() === "new") return false;
  return true;
}

export function savedGradeBg(grade: string | number | null | undefined): string {
  const n = typeof grade === "number" ? grade : Number.parseFloat(String(grade ?? ""));
  if (!Number.isFinite(n)) return "#10b981";
  if (n >= 9) return "#10b981";
  if (n >= 7) return "#f59e0b";
  return "#f97316";
}

export type SavedConditionLook = { className: string };

export function savedConditionLook(condition: string | null | undefined): SavedConditionLook {
  const lower = (condition ?? "").trim().toLowerCase();
  if (lower === "new" || lower === "neuf") {
    return { className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" };
  }
  if (lower === "custom" || lower === "personnalisé" || lower === "personnalise") {
    return { className: "bg-amber-500/15 text-amber-600 dark:text-amber-400" };
  }
  return { className: "bg-muted text-muted-foreground" };
}

export type SavedActionLook = {
  labelKey: "actionBuy" | "actionOffer" | "actionBid" | "actionNotify" | "actionFindSimilar";
  icon: "buyNow" | "offer" | "bid" | "reminder" | "search";
  className: string;
  disabled: boolean;
};

export function savedActionLook(action: SavedItemAction): SavedActionLook {
  switch (action) {
    case "buy":
      return {
        labelKey: "actionBuy",
        icon: "buyNow",
        className: "bg-foreground text-background",
        disabled: false,
      };
    case "offer":
      return {
        labelKey: "actionOffer",
        icon: "offer",
        className: "bg-foreground text-background",
        disabled: false,
      };
    case "bid":
      return {
        labelKey: "actionBid",
        icon: "bid",
        className: "bg-foreground text-background",
        disabled: false,
      };
    case "notify":
      return {
        labelKey: "actionNotify",
        icon: "reminder",
        className: "bg-foreground text-background",
        disabled: false,
      };
    case "find_similar":
      return {
        labelKey: "actionFindSimilar",
        icon: "search",
        className: "border border-border bg-transparent text-foreground",
        disabled: false,
      };
  }
}
